import React, { useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import {
    IReminderAnchor,
    IReminderMetadata,
    IReminderPreview,
    IReminderRule,
    IReminderRuleWritable,
} from '../Interfaces/IReminderRule';
import { ISystemMail } from '../Interfaces/ISystemMail';
import { IReservationStatus } from '../Interfaces/IReservation';
import {
    extractReminderErrorMessage,
    useActivateReminderRule,
    useCreateReminderRule,
    useDeactivateReminderRule,
    useDeleteReminderRule,
    usePreviewReminderRule,
    useReminderMetadata,
    useReminderRules,
    useTestSendReminderRule,
    useUpdateReminderRule,
} from '../hooks/reminderRules';
import { useGetSystemMail } from '../hooks/mailing';
import { useGetReservationStatus } from '../hooks/reservations';
import { useAlert } from '../core/AlertContext';
import RowActionsMenu from '../core/RowActionsMenu';
import { BrandedDataGridOverlay } from '../core/BrandedDataGridOverlay';
import DetailScreenHeader from '../core/DetailScreenHeader';
import { EVENT_DIALOG_MAX_WIDTH, EVENT_DIALOG_PAPER_SX } from '../calendar/dialogLayout';
import PageHeader from '../core/PageHeader';
import PvtaCard from '../core/PvtaCard';
import { STATUS_TONES, dangerButtonSx, fieldSx, primaryButtonSx, secondaryButtonSx } from '../core/pvtaStyles';

type OffsetUnit = 'minutes' | 'hours' | 'days';
type OffsetDirection = 'before' | 'after';

const UNIT_SECONDS: Record<OffsetUnit, number> = { minutes: 60, hours: 3600, days: 86400 };
const UNIT_LABEL_SINGULAR: Record<OffsetUnit, string> = { minutes: 'Minute', hours: 'Stunde', days: 'Tag' };
const UNIT_LABEL_PLURAL: Record<OffsetUnit, string> = { minutes: 'Minuten', hours: 'Stunden', days: 'Tage' };

/** Zerlegt einen vorzeichenbehafteten Sekundenwert in Wert + größtmögliche ganze Einheit + Richtung. */
const decomposeOffsetSeconds = (
    offsetSeconds: number,
): { value: number; unit: OffsetUnit; direction: OffsetDirection } => {
    const direction: OffsetDirection = offsetSeconds > 0 ? 'after' : 'before';
    const abs = Math.abs(offsetSeconds);
    const units: OffsetUnit[] = ['days', 'hours', 'minutes'];
    for (const unit of units) {
        if (abs !== 0 && abs % UNIT_SECONDS[unit] === 0) {
            return { value: abs / UNIT_SECONDS[unit], unit, direction };
        }
    }
    return { value: Math.round(abs / 60), unit: 'minutes', direction };
};

const composeOffsetSeconds = (value: number, unit: OffsetUnit, direction: OffsetDirection): number => {
    const magnitude = Math.max(0, value) * UNIT_SECONDS[unit];
    return direction === 'before' ? -magnitude : magnitude;
};

const formatOffsetLabel = (offsetSeconds: number): string => {
    const { value, unit, direction } = decomposeOffsetSeconds(offsetSeconds);
    const unitLabel = value === 1 ? UNIT_LABEL_SINGULAR[unit] : UNIT_LABEL_PLURAL[unit];
    return `${value} ${unitLabel} ${direction === 'before' ? 'vorher' : 'nachher'}`;
};

const toTimeInputValue = (sendTimeLocal: string | null | undefined): string =>
    sendTimeLocal ? sendTimeLocal.slice(0, 5) : '';

const toApiTimeValue = (value: string): string | null => (value ? `${value}:00` : null);

const getAnchor = (metadata: IReminderMetadata | undefined, key: string): IReminderAnchor | undefined =>
    metadata?.anchors.find((anchor) => anchor.key === key);

interface RuleFormState {
    name: string;
    trigger: string;
    offsetValue: number;
    offsetUnit: OffsetUnit;
    offsetDirection: OffsetDirection;
    sendTimeLocal: string;
    audience: string;
    guard: string;
    expectedStatus: number | '';
    systemMail: number | '';
    senderAccount: string;
    channel: string;
    mandatory: boolean;
    maxLatenessSeconds: number;
}

const buildDefaultFormState = (metadata: IReminderMetadata | undefined): RuleFormState => {
    const anchor = metadata?.anchors[0];
    return {
        name: '',
        trigger: anchor?.key ?? '',
        offsetValue: 0,
        offsetUnit: 'minutes',
        offsetDirection: 'before',
        sendTimeLocal: '',
        audience: anchor?.allowed_audiences[0] ?? '',
        guard: anchor?.allowed_guards[0] ?? 'none',
        expectedStatus: '',
        systemMail: '',
        senderAccount: metadata?.sender_accounts[0] ?? '',
        channel: metadata?.channels[0] ?? 'email',
        mandatory: true,
        maxLatenessSeconds: 21600,
    };
};

const buildFormStateFromRule = (rule: IReminderRule): RuleFormState => {
    const { value, unit, direction } = decomposeOffsetSeconds(rule.offset_seconds);
    return {
        name: rule.name,
        trigger: rule.trigger,
        offsetValue: value,
        offsetUnit: unit,
        offsetDirection: direction,
        sendTimeLocal: toTimeInputValue(rule.send_time_local),
        audience: rule.audience,
        guard: rule.guard,
        expectedStatus: rule.expected_status ?? '',
        systemMail: rule.system_mail,
        senderAccount: rule.sender_account,
        channel: rule.channel,
        mandatory: rule.mandatory,
        maxLatenessSeconds: rule.max_lateness_seconds,
    };
};

interface ReminderRuleFormDialogProps {
    rule: IReminderRule | null;
    metadata: IReminderMetadata | undefined;
    isLoadingMetadata: boolean;
    systemMails: ISystemMail[];
    reservationStatuses: IReservationStatus[];
    onClose: () => void;
}

const ReminderRuleFormDialog: React.FC<ReminderRuleFormDialogProps> = ({
    rule,
    metadata,
    isLoadingMetadata,
    systemMails,
    reservationStatuses,
    onClose,
}) => {
    const isCreateMode = rule === null;
    const { showAlert } = useAlert();
    const { createReminderRule, isCreatingReminderRule } = useCreateReminderRule();
    const { updateReminderRule, isUpdatingReminderRule } = useUpdateReminderRule();
    const isSaving = isCreatingReminderRule || isUpdatingReminderRule;

    const [form, setForm] = useState<RuleFormState>(() =>
        rule ? buildFormStateFromRule(rule) : buildDefaultFormState(metadata),
    );

    const anchor = getAnchor(metadata, form.trigger);

    const handleTriggerChange = (event: SelectChangeEvent) => {
        const newTrigger = event.target.value;
        const newAnchor = getAnchor(metadata, newTrigger);
        setForm((prev) => ({
            ...prev,
            trigger: newTrigger,
            audience: newAnchor?.allowed_audiences.includes(prev.audience)
                ? prev.audience
                : newAnchor?.allowed_audiences[0] ?? '',
            guard: newAnchor?.allowed_guards.includes(prev.guard) ? prev.guard : newAnchor?.allowed_guards[0] ?? 'none',
            expectedStatus: newAnchor?.expected_status_mode === 'forbidden' ? '' : prev.expectedStatus,
            sendTimeLocal: newAnchor?.anchor_type === 'date' ? prev.sendTimeLocal : '',
        }));
    };

    const isFormValid = Boolean(
        form.name.trim() &&
            anchor &&
            form.systemMail !== '' &&
            form.senderAccount &&
            form.channel &&
            form.audience &&
            form.guard &&
            (anchor.expected_status_mode !== 'required' || form.expectedStatus !== '') &&
            (anchor.anchor_type !== 'date' || form.sendTimeLocal !== ''),
    );

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!anchor || !isFormValid) {
            return;
        }
        const payload: IReminderRuleWritable = {
            name: form.name.trim(),
            trigger: form.trigger,
            offset_seconds: composeOffsetSeconds(form.offsetValue, form.offsetUnit, form.offsetDirection),
            send_time_local: anchor.anchor_type === 'date' ? toApiTimeValue(form.sendTimeLocal) : null,
            audience: form.audience,
            guard: form.guard,
            expected_status:
                anchor.expected_status_mode === 'forbidden' || form.expectedStatus === '' ? null : form.expectedStatus,
            system_mail: form.systemMail === '' ? -1 : form.systemMail,
            sender_account: form.senderAccount,
            channel: form.channel,
            mandatory: form.mandatory,
            max_lateness_seconds: form.maxLatenessSeconds,
        };
        try {
            if (isCreateMode) {
                await createReminderRule(payload);
                showAlert({ text: 'Regel erfolgreich angelegt.' });
            } else if (rule) {
                await updateReminderRule({ id: rule.id, ...payload });
                showAlert({ text: 'Regel erfolgreich aktualisiert.' });
            }
            onClose();
        } catch (error) {
            showAlert({
                severity: 'error',
                text:
                    extractReminderErrorMessage(error) ??
                    (isCreateMode ? 'Fehler beim Anlegen der Regel.' : 'Fehler beim Aktualisieren der Regel.'),
            });
        }
    };

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth={EVENT_DIALOG_MAX_WIDTH}
            fullWidth
            PaperProps={EVENT_DIALOG_PAPER_SX}
        >
            <DetailScreenHeader
                title={isCreateMode ? 'Erinnerungsregel anlegen' : 'Erinnerungsregel bearbeiten'}
                onClose={onClose}
            />
            <DialogContent sx={{ pt: 2 }}>
                {isLoadingMetadata || !metadata ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box component="form" onSubmit={(event: React.FormEvent<HTMLFormElement>) => { void handleSubmit(event); }} sx={{ minWidth: 0 }}>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    label="Name"
                                    value={form.name}
                                    onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
                                    required
                                    fullWidth
                                    size="small"
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small" sx={fieldSx}>
                                    <InputLabel id="reminder-trigger-label">Trigger</InputLabel>
                                    <Select
                                        labelId="reminder-trigger-label"
                                        label="Trigger"
                                        value={form.trigger}
                                        onChange={handleTriggerChange}
                                    >
                                        {(metadata.anchors ?? []).map((anchorOption) => (
                                            <MenuItem key={anchorOption.key} value={anchorOption.key}>
                                                {anchorOption.key}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <Stack direction="row" spacing={1}>
                                    <TextField
                                        label="Abstand"
                                        type="number"
                                        value={form.offsetValue}
                                        onChange={(event) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                offsetValue: Math.max(0, Number(event.target.value)),
                                            }))
                                        }
                                        inputProps={{ min: 0 }}
                                        size="small"
                                        sx={{ ...fieldSx, width: 100 }}
                                    />
                                    <FormControl size="small" sx={{ ...fieldSx, minWidth: 120 }}>
                                        <InputLabel id="reminder-offset-unit-label">Einheit</InputLabel>
                                        <Select
                                            labelId="reminder-offset-unit-label"
                                            label="Einheit"
                                            value={form.offsetUnit}
                                            onChange={(event) =>
                                                setForm((prev) => ({ ...prev, offsetUnit: event.target.value as OffsetUnit }))
                                            }
                                        >
                                            <MenuItem value="minutes">Minuten</MenuItem>
                                            <MenuItem value="hours">Stunden</MenuItem>
                                            <MenuItem value="days">Tage</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small" sx={{ ...fieldSx, minWidth: 150 }}>
                                        <InputLabel id="reminder-offset-direction-label">Richtung</InputLabel>
                                        <Select
                                            labelId="reminder-offset-direction-label"
                                            label="Richtung"
                                            value={form.offsetDirection}
                                            onChange={(event) =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    offsetDirection: event.target.value as OffsetDirection,
                                                }))
                                            }
                                        >
                                            <MenuItem value="before">vor dem Anker</MenuItem>
                                            <MenuItem value="after">nach dem Anker</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Stack>
                            </Grid>
                            {anchor?.anchor_type === 'date' && (
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Sendezeit (lokal)"
                                        type="time"
                                        value={form.sendTimeLocal}
                                        onChange={(event) =>
                                            setForm((prev) => ({ ...prev, sendTimeLocal: event.target.value }))
                                        }
                                        required
                                        fullWidth
                                        size="small"
                                        InputLabelProps={{ shrink: true }}
                                        sx={fieldSx}
                                    />
                                </Grid>
                            )}
                            {anchor && anchor.expected_status_mode !== 'forbidden' && (
                                <Grid item xs={12} sm={6}>
                                    <FormControl
                                        fullWidth
                                        required={anchor.expected_status_mode === 'required'}
                                        size="small"
                                        sx={fieldSx}
                                    >
                                        <InputLabel id="reminder-expected-status-label">Erwarteter Buchungsstatus</InputLabel>
                                        <Select
                                            labelId="reminder-expected-status-label"
                                            label="Erwarteter Buchungsstatus"
                                            value={form.expectedStatus}
                                            onChange={(event) =>
                                                setForm((prev) => ({
                                                    ...prev,
                                                    expectedStatus: event.target.value === '' ? '' : Number(event.target.value),
                                                }))
                                            }
                                        >
                                            {anchor.expected_status_mode === 'optional' && (
                                                <MenuItem value="">—</MenuItem>
                                            )}
                                            {reservationStatuses.map((statusOption) => (
                                                <MenuItem key={statusOption.id} value={statusOption.id}>
                                                    {statusOption.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small" sx={fieldSx}>
                                    <InputLabel id="reminder-audience-label">Zielgruppe</InputLabel>
                                    <Select
                                        labelId="reminder-audience-label"
                                        label="Zielgruppe"
                                        value={form.audience}
                                        onChange={(event) => setForm((prev) => ({ ...prev, audience: event.target.value }))}
                                    >
                                        {(anchor?.allowed_audiences ?? []).map((audienceOption) => (
                                            <MenuItem key={audienceOption} value={audienceOption}>
                                                {audienceOption}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small" sx={fieldSx}>
                                    <InputLabel id="reminder-guard-label">Guard</InputLabel>
                                    <Select
                                        labelId="reminder-guard-label"
                                        label="Guard"
                                        value={form.guard}
                                        onChange={(event) => setForm((prev) => ({ ...prev, guard: event.target.value }))}
                                    >
                                        {(anchor?.allowed_guards ?? []).map((guardOption) => (
                                            <MenuItem key={guardOption} value={guardOption}>
                                                {guardOption}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small" sx={fieldSx}>
                                    <InputLabel id="reminder-system-mail-label">Systemnachricht</InputLabel>
                                    <Select
                                        labelId="reminder-system-mail-label"
                                        label="Systemnachricht"
                                        value={form.systemMail}
                                        onChange={(event) =>
                                            setForm((prev) => ({
                                                ...prev,
                                                systemMail: event.target.value === '' ? '' : Number(event.target.value),
                                            }))
                                        }
                                    >
                                        {systemMails.map((mail) => (
                                            <MenuItem key={mail.id} value={mail.id}>
                                                {mail.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                                {anchor && anchor.allowed_template_vars.length > 0 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        Verfügbare Vorlagen-Variablen: {anchor.allowed_template_vars.map((v) => `{{${v}}}`).join(', ')}
                                    </Typography>
                                )}
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small" sx={fieldSx}>
                                    <InputLabel id="reminder-sender-account-label">Absender-Konto</InputLabel>
                                    <Select
                                        labelId="reminder-sender-account-label"
                                        label="Absender-Konto"
                                        value={form.senderAccount}
                                        onChange={(event) =>
                                            setForm((prev) => ({ ...prev, senderAccount: event.target.value }))
                                        }
                                    >
                                        {metadata.sender_accounts.map((account) => (
                                            <MenuItem key={account} value={account}>
                                                {account}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth required size="small" sx={fieldSx}>
                                    <InputLabel id="reminder-channel-label">Kanal</InputLabel>
                                    <Select
                                        labelId="reminder-channel-label"
                                        label="Kanal"
                                        value={form.channel}
                                        onChange={(event) => setForm((prev) => ({ ...prev, channel: event.target.value }))}
                                    >
                                        {metadata.channels.map((channelOption) => (
                                            <MenuItem key={channelOption} value={channelOption}>
                                                {channelOption}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Max. Verspätung (Sekunden)"
                                    type="number"
                                    value={form.maxLatenessSeconds}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            maxLatenessSeconds: Math.max(0, Number(event.target.value)),
                                        }))
                                    }
                                    inputProps={{ min: 0 }}
                                    fullWidth
                                    size="small"
                                    sx={fieldSx}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={form.mandatory}
                                            onChange={(event) =>
                                                setForm((prev) => ({ ...prev, mandatory: event.target.checked }))
                                            }
                                        />
                                    }
                                    label="Verpflichtend (harte Job-Erwartung)"
                                />
                            </Grid>
                        </Grid>
                        <Divider sx={{ my: 2 }} />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 1.5,
                                flexDirection: { xs: 'column-reverse', sm: 'row' },
                            }}
                        >
                            <Button onClick={onClose} sx={{ ...secondaryButtonSx, width: { xs: '100%', sm: 'auto' } }}>
                                Abbrechen
                            </Button>
                            <Button
                                type="submit"
                                disabled={isSaving || !isFormValid}
                                sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                            >
                                {isSaving ? 'Speichern...' : 'Speichern'}
                            </Button>
                        </Box>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
};

type PreviewDialogMode = 'view' | 'activate';

const ReminderRulesSettings: React.FC = () => {
    const { rules, isLoadingRules, isErrorRules } = useReminderRules();
    const { metadata, isLoadingMetadata } = useReminderMetadata();
    const { systemMailArray } = useGetSystemMail();
    const { reservationStatusArray } = useGetReservationStatus();
    const { deleteReminderRule, isDeletingReminderRule } = useDeleteReminderRule();
    const { activateReminderRule, isActivatingReminderRule } = useActivateReminderRule();
    const { deactivateReminderRule } = useDeactivateReminderRule();
    const { previewReminderRule } = usePreviewReminderRule();
    const { testSendReminderRule, isTestSendingReminderRule } = useTestSendReminderRule();
    const { showAlert } = useAlert();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [editState, setEditState] = useState<{ rule: IReminderRule | null } | null>(null);
    const [deleteCandidate, setDeleteCandidate] = useState<IReminderRule | null>(null);
    const [previewDialog, setPreviewDialog] = useState<{ rule: IReminderRule; mode: PreviewDialogMode } | null>(null);
    const [previewData, setPreviewData] = useState<IReminderPreview | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [testSendCandidate, setTestSendCandidate] = useState<IReminderRule | null>(null);
    const [testSendTo, setTestSendTo] = useState('');

    const handleOpenPreview = async (rule: IReminderRule, mode: PreviewDialogMode) => {
        setPreviewData(null);
        setPreviewError(null);
        setPreviewDialog({ rule, mode });
        try {
            const data = await previewReminderRule(rule.id);
            setPreviewData(data);
        } catch {
            setPreviewError('Vorschau konnte nicht geladen werden.');
        }
    };

    const handleConfirmActivate = async () => {
        if (!previewDialog) {
            return;
        }
        try {
            await activateReminderRule(previewDialog.rule.id);
            showAlert({ text: 'Regel aktiviert.' });
            setPreviewDialog(null);
        } catch (error) {
            showAlert({
                severity: 'error',
                text: extractReminderErrorMessage(error) ?? 'Aktivierung fehlgeschlagen.',
            });
            setPreviewDialog(null);
        }
    };

    const handleDeactivate = async (rule: IReminderRule) => {
        try {
            await deactivateReminderRule(rule.id);
            showAlert({ text: 'Regel deaktiviert.' });
        } catch (error) {
            showAlert({ severity: 'error', text: extractReminderErrorMessage(error) ?? 'Deaktivieren fehlgeschlagen.' });
        }
    };

    const handleDelete = async () => {
        if (!deleteCandidate) {
            return;
        }
        try {
            await deleteReminderRule(deleteCandidate.id);
            showAlert({ text: 'Regel erfolgreich gelöscht.' });
            setDeleteCandidate(null);
        } catch (error) {
            showAlert({
                severity: 'error',
                text:
                    extractReminderErrorMessage(error) ??
                    'Löschen fehlgeschlagen. Die Regel wird evtl. noch von Erinnerungs-Jobs referenziert.',
            });
        }
    };

    const handleTestSendSubmit = async () => {
        if (!testSendCandidate) {
            return;
        }
        try {
            const result = await testSendReminderRule({
                id: testSendCandidate.id,
                to: testSendTo.trim() || undefined,
            });
            showAlert({ text: `${result.detail} (${result.to})` });
            setTestSendCandidate(null);
        } catch (error) {
            showAlert({ severity: 'error', text: extractReminderErrorMessage(error) ?? 'Testversand fehlgeschlagen.' });
        }
    };

    const renderRowActions = (rule: IReminderRule) => (
        <RowActionsMenu
            items={[
                {
                    label: 'Bearbeiten',
                    icon: <EditOutlinedIcon fontSize="small" />,
                    onClick: () => setEditState({ rule }),
                },
                rule.is_active
                    ? {
                          label: 'Deaktivieren',
                          icon: <PauseCircleOutlineIcon fontSize="small" />,
                          onClick: () => {
                              void handleDeactivate(rule);
                          },
                      }
                    : {
                          label: 'Aktivieren',
                          icon: <PlayCircleOutlineIcon fontSize="small" />,
                          onClick: () => {
                              void handleOpenPreview(rule, 'activate');
                          },
                      },
                {
                    label: 'Vorschau',
                    icon: <VisibilityOutlinedIcon fontSize="small" />,
                    onClick: () => {
                        void handleOpenPreview(rule, 'view');
                    },
                },
                {
                    label: 'Testversand',
                    icon: <SendOutlinedIcon fontSize="small" />,
                    onClick: () => {
                        setTestSendTo('');
                        setTestSendCandidate(rule);
                    },
                },
                {
                    label: 'Löschen',
                    icon: <DeleteOutlineIcon fontSize="small" />,
                    onClick: () => setDeleteCandidate(rule),
                },
            ]}
        />
    );

    const renderStatusChip = (isActive: boolean) => (
        <Chip
            label={isActive ? 'Aktiv' : 'Inaktiv'}
            size="small"
            sx={
                isActive
                    ? { backgroundColor: STATUS_TONES.success.bg, color: STATUS_TONES.success.color }
                    : { backgroundColor: STATUS_TONES.neutral.bg, color: STATUS_TONES.neutral.color }
            }
        />
    );

    const columns: GridColDef<IReminderRule>[] = [
        { field: 'name', headerName: 'Name', flex: 1.1, minWidth: 180 },
        { field: 'trigger', headerName: 'Trigger', flex: 0.9, minWidth: 150 },
        { field: 'audience', headerName: 'Zielgruppe', flex: 0.9, minWidth: 150 },
        {
            field: 'offset_seconds',
            headerName: 'Abstand',
            flex: 0.9,
            minWidth: 160,
            valueGetter: (_value: unknown, row: IReminderRule) => formatOffsetLabel(row.offset_seconds),
        },
        {
            field: 'is_active',
            headerName: 'Status',
            flex: 0.6,
            minWidth: 110,
            renderCell: (params: GridRenderCellParams<IReminderRule>) => renderStatusChip(params.row.is_active),
        },
        {
            field: 'actions',
            headerName: 'Aktionen',
            sortable: false,
            filterable: false,
            minWidth: 90,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: GridRenderCellParams<IReminderRule>) => renderRowActions(params.row),
        },
    ];

    if (isLoadingRules) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isErrorRules || !rules) {
        return (
            <Alert severity="error">
                Die Erinnerungsregeln konnten nicht geladen werden. Bitte später erneut versuchen.
            </Alert>
        );
    }

    return (
        <Box sx={{ py: { xs: 3, md: 4 } }}>
            <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2 } }}>
                <PageHeader title="Erinnerungen" subtitle="Erinnerungsregeln für automatische Erinnerungsmails verwalten." />
                <PvtaCard
                    large
                    sx={{ p: { xs: 2, sm: 2.5 }, width: '100%', maxWidth: '100%', minWidth: 0, boxSizing: 'border-box' }}
                >
                    <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={() => setEditState({ rule: null })}
                            // Anlegen erst, wenn die Metadaten (Anker/Empfänger/Guards) geladen sind:
                            // sonst startet das Formular ohne sinnvolle Trigger-/Feld-Defaults.
                            disabled={isLoadingMetadata || !metadata}
                            sx={{ ...primaryButtonSx, width: { xs: '100%', sm: 'auto' } }}
                        >
                            Neue Regel
                        </Button>
                    </Stack>
                    {!isMobile ? (
                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                            <DataGrid
                                rows={rules}
                                columns={columns}
                                disableRowSelectionOnClick
                                autoHeight
                                pageSizeOptions={[10, 25, 50]}
                                slots={{
                                    noRowsOverlay: BrandedDataGridOverlay,
                                    noResultsOverlay: BrandedDataGridOverlay,
                                }}
                                sx={{ border: 0 }}
                            />
                        </Box>
                    ) : (
                        <Stack spacing={1.5}>
                            {rules.map((rule) => (
                                <Card key={rule.id} variant="outlined">
                                    <CardContent>
                                        <Stack spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                                                        {rule.name}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {rule.trigger} · {rule.audience}
                                                    </Typography>
                                                </Box>
                                                {renderRowActions(rule)}
                                            </Stack>
                                            <Typography variant="body2">{formatOffsetLabel(rule.offset_seconds)}</Typography>
                                            <Box>{renderStatusChip(rule.is_active)}</Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            ))}
                        </Stack>
                    )}
                </PvtaCard>

                {editState && (
                    <ReminderRuleFormDialog
                        key={editState.rule?.id ?? 'new'}
                        rule={editState.rule}
                        metadata={metadata}
                        isLoadingMetadata={isLoadingMetadata}
                        systemMails={systemMailArray ?? []}
                        reservationStatuses={reservationStatusArray}
                        onClose={() => setEditState(null)}
                    />
                )}

                <Dialog open={deleteCandidate !== null} onClose={() => setDeleteCandidate(null)}>
                    <DialogTitle>Regel löschen?</DialogTitle>
                    <DialogContent>
                        <Typography variant="body2">
                            Soll die Regel &quot;{deleteCandidate?.name}&quot; wirklich gelöscht werden?
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Hinweis: Regeln, auf die bereits Erinnerungs-Jobs verweisen, können nicht gelöscht werden.
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setDeleteCandidate(null)} sx={secondaryButtonSx}>
                            Abbrechen
                        </Button>
                        <Button
                            sx={dangerButtonSx}
                            disabled={isDeletingReminderRule}
                            onClick={() => {
                                void handleDelete();
                            }}
                        >
                            Löschen
                        </Button>
                    </DialogActions>
                </Dialog>

                <Dialog open={previewDialog !== null} onClose={() => setPreviewDialog(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>
                        {previewDialog?.mode === 'activate' ? 'Regel aktivieren?' : 'Vorschau'}
                        {previewDialog ? `: ${previewDialog.rule.name}` : ''}
                    </DialogTitle>
                    <DialogContent>
                        {!previewData && !previewError && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={28} />
                            </Box>
                        )}
                        {previewError && <Alert severity="error">{previewError}</Alert>}
                        {previewData && (
                            <Stack spacing={0.75} sx={{ mt: 1 }}>
                                <Typography variant="body2">Neue Jobs: {previewData.new_jobs}</Typography>
                                <Typography variant="body2">Bestehende Jobs: {previewData.existing_jobs}</Typography>
                                <Typography variant="body2">Fällige Jobs: {previewData.due_jobs}</Typography>
                                <Typography variant="body2">Erwartete Empfänger: {previewData.expected_recipients}</Typography>
                                <Typography variant="body2">Ungültige Empfänger: {previewData.invalid_recipients}</Typography>
                                <Typography variant="body2">Erwartete Versendungen: {previewData.expected_sends}</Typography>
                                {previewDialog?.mode === 'activate' && (
                                    <Alert severity="warning" sx={{ mt: 1 }}>
                                        Bitte die Zahlen sorgfältig prüfen — nach der Aktivierung werden fällige
                                        Erinnerungen automatisch versendet.
                                    </Alert>
                                )}
                            </Stack>
                        )}
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setPreviewDialog(null)} sx={secondaryButtonSx}>
                            {previewDialog?.mode === 'activate' ? 'Abbrechen' : 'Schließen'}
                        </Button>
                        {previewDialog?.mode === 'activate' && (
                            <Button
                                sx={primaryButtonSx}
                                disabled={!previewData || isActivatingReminderRule}
                                onClick={() => {
                                    void handleConfirmActivate();
                                }}
                            >
                                Aktivieren
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>

                <Dialog open={testSendCandidate !== null} onClose={() => setTestSendCandidate(null)} maxWidth="xs" fullWidth>
                    <DialogTitle>Testversand{testSendCandidate ? `: ${testSendCandidate.name}` : ''}</DialogTitle>
                    <DialogContent>
                        <TextField
                            label="An (optional)"
                            placeholder="eigene E-Mail-Adresse"
                            value={testSendTo}
                            onChange={(event) => setTestSendTo(event.target.value)}
                            fullWidth
                            size="small"
                            sx={{ ...fieldSx, mt: 1 }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 2 }}>
                        <Button onClick={() => setTestSendCandidate(null)} sx={secondaryButtonSx}>
                            Abbrechen
                        </Button>
                        <Button
                            sx={primaryButtonSx}
                            disabled={isTestSendingReminderRule}
                            onClick={() => {
                                void handleTestSendSubmit();
                            }}
                        >
                            Senden
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default ReminderRulesSettings;
